import Bars from '@gef-ui/material-icons/Bars';
import BarsMaximizeRegular from '@gef-ui/material-icons/BarsMaximizeRegular';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import useIsMdAndUp from '../../../hooks/useIsMdAndUp';
import useIsXlAndUp from '../../../hooks/useIsXlAndUp';
import CompanyLogoFull from '../../Logo/CompanyLogoFull';
import { APPBAR_ICON_SIDE, HEADER } from '../../sizeConstants';
import { sxPropType, useDefaultComponentValue } from '../../utils';
import AppbarButton from '../AppbarButton';
import AppbarDivider from '../AppbarDivider';
import { ResponsiveContextProvider } from '../ResponsiveContext';
const RootStyle = styled(AppBar)(({ theme })=>({
        backgroundColor: theme.palette.primary.main,
        borderRadius: 0,
        boxShadow: 'none',
        width: '100%',
        position: 'fixed',
        height: HEADER.MOBILE_HEIGHT,
        transition: theme.transitions.create([
            'width',
            'height'
        ], {
            duration: theme.transitions.duration.shorter
        }),
        [theme.breakpoints.up('md')]: {
            height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
            width: '100%'
        }
    }));
const DesktopLogo = styled(CompanyLogoFull)(({ theme })=>({
        [theme.breakpoints.down('md')]: {
            maxHeight: HEADER.LOGO_MOBILE_HEIGHT
        },
        [theme.breakpoints.up('md')]: {
            height: HEADER.LOGO_DESKTOP_HEIGHT
        },
        width: 'auto'
    }));
const COMPONENT_NAME = 'Appbar';
const Appbar = /*#__PURE__*/ React.forwardRef((inProps, ref)=>{
    const props = useDefaultComponentValue({
        inProps,
        name: COMPONENT_NAME
    });
    const { withSidebar, onOpenSidebar, onSidebarClick, rightSideElements = [], onLogoClick, afterLogoElement, appName, customLogo, sx } = props;
    const isMdAndUp = useIsMdAndUp();
    const isXlAndUp = useIsXlAndUp();
    const [visibleElements, hiddenElements] = useMemo(()=>{
        if (isMdAndUp) {
            return [
                rightSideElements,
                []
            ];
        }
        return [
            rightSideElements.filter((element)=>!element.props?.['data-hide']),
            rightSideElements.filter((element)=>element.props?.['data-hide'])
        ];
    }, [
        rightSideElements,
        isMdAndUp
    ]);
    return /*#__PURE__*/ React.createElement(RootStyle, {
        ref: ref,
        sx: sx
    }, /*#__PURE__*/ React.createElement(Toolbar, {
        sx: {
            flexGrow: 1,
            pl: {
                md: 2.5,
                xl: 4,
                xs: 1
            },
            pr: {
                md: 4,
                xs: 2
            },
            minHeight: {
                xs: HEADER.MOBILE_HEIGHT,
                md: HEADER.DASHBOARD_DESKTOP_HEIGHT
            }
        }
    }, /*#__PURE__*/ React.createElement(Collapse, {
        orientation: "horizontal",
        in: !isXlAndUp && withSidebar,
        mountOnEnter: true,
        unmountOnExit: true,
        component: "span"
    }, /*#__PURE__*/ React.createElement(Stack, {
        direction: "row"
    }, /*#__PURE__*/ React.createElement(AppbarButton, {
        onClick: (e)=>{
            onOpenSidebar(e);
            onSidebarClick?.(e);
        }
    }, isMdAndUp ? /*#__PURE__*/ React.createElement(BarsMaximizeRegular, {
        sx: {
            width: APPBAR_ICON_SIDE,
            heigth: APPBAR_ICON_SIDE
        }
    }) : /*#__PURE__*/ React.createElement(Bars, {
        sx: {
            width: APPBAR_ICON_SIDE,
            heigth: APPBAR_ICON_SIDE
        }
    })), /*#__PURE__*/ React.createElement(Divider, {
        sx: [
            {
                ml: 1,
                mr: 2,
                borderColor: 'white.shades.12p'
            }
        ],
        orientation: "vertical",
        flexItem: true
    }))), /*#__PURE__*/ React.createElement(ButtonBase, {
        disableRipple: true,
        ...onLogoClick ? {
            onClick: onLogoClick
        } : {
            href: '/'
        }
    }, customLogo ?? /*#__PURE__*/ React.createElement(DesktopLogo, {
        color: "white"
    })), !!appName && isMdAndUp && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(AppbarDivider, null), typeof appName === 'string' ? /*#__PURE__*/ React.createElement(Typography, null, appName) : appName), afterLogoElement, /*#__PURE__*/ React.createElement(Box, {
        sx: {
            flexGrow: 1
        }
    }), /*#__PURE__*/ React.createElement(ResponsiveContextProvider, {
        value: hiddenElements
    }, /*#__PURE__*/ React.createElement(Stack, {
        direction: "row",
        spacing: {
            xs: 1.5,
            sm: 1.5
        },
        sx: {
            alignItems: 'center'
        }
    }, visibleElements))));
});
Appbar.displayName = COMPONENT_NAME;
Appbar.propTypes = {
    afterLogoElement: PropTypes.node,
    appName: PropTypes.node,
    customLogo: PropTypes.node,
    onLogoClick: PropTypes.func,
    /**
	 * Use this prop alongside "withSidebar" to open your sidebar on hamburger click (mobile only)
	 */ onOpenSidebar: PropTypes.func,
    onSidebarClick: PropTypes.func,
    rightSideElements: PropTypes.arrayOf(PropTypes.node),
    sx: sxPropType,
    /**
	 * Use this prop alongside "onOpenSidebar" to open your sidebar on hamburger click (mobile only)
	 */ withSidebar: PropTypes.bool
};
export default Appbar;
